/* eslint-disable react/jsx-filename-extension, react/static-property-placement, max-len */

import React from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import withProprtionalResponsiveAttributes from 'services/withProportionalResponsiveAttributes';
import ArticleImg from './article-img';
import ArticleMediaIcon from './article-media-icon';

/**
 * A React component for the innards of an article in a list, and the analogue
 * to the PHP template part of the same name.
 */
export default class ContentArticleList extends React.PureComponent {
  /**
   * Most of these props mimic the variables passed to the PHP template, but,
   * because they aren't bound by restrictions on naming, they can use the names
   * of the original properties on the article.
   *
   * The component includes only the logic currently necessary to render article
   * list layouts eligible for infinite-scroll. For example, no so long as no
   * layouts containing excerpts can get infinite scroll, excerpts are not
   * passed to the component nor rendered.
   *
   * Some props are not part of the PHP template. Typically, they represent
   * contextual information available to templates contextually in PHP, not
   * variables passed to the template part.
   */
  static propTypes = {
    listId: PropTypes.string.isRequired,
    h: PropTypes.number.isRequired,
    link: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    excerpt_field: PropTypes.string.isRequired,
    thumbnail: PropTypes.shape({
      src: PropTypes.string,
      alt_text: PropTypes.string,
      proportional_sources: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    }).isRequired,
    category: PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.string,
    }).isRequired,
    date: PropTypes.shape({
      date: PropTypes.string,
      datetime: PropTypes.string,
    }).isRequired,
    site_model: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    context: PropTypes.string.isRequired,
    lead_media: PropTypes.shape({
      format: PropTypes.string,
      raw: PropTypes.string,
      type: PropTypes.string,
    }).isRequired,
    media_type_icon: PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
    }).isRequired,
    showThumb: PropTypes.string.isRequired,
    default_ap_image: PropTypes.shape({
      ap_default_orientation: PropTypes.string,
      ap_default_src: PropTypes.string,
      ap_default_alt_text: PropTypes.string,
    }).isRequired,
  };

  constructor(props) {
    super(props);

    /**
     * Each article list is given a `listId` server-side. The list ID is used to
     * identify both the outer article list itself and the articles within it so
     * that this component can find an article that is part of the list in the
     * DOM that it can scrape for props.
     */
    const model = document.querySelector(
      `[data-article-list-id="${props.listId}"]`,
    );

    if (! model) {
      throw new Error('Missing article model.');
    }

    /**
     * Much like the reactComponentManager, this component scrapes DOM data
     * attributes to find configuration data. While perhaps not ideal, this
     * provides some parity with the PHP template, which also uses some values
     * plucked out of other state rather than passed by injection.
     */
    const { articleTextClasses, articleThumbClasses } = model.dataset;

    this.state = {
      articleTextClasses,
      articleThumbClasses,
      imageContext: JSON.parse(model.dataset.currentImageContext),
    };
  }

  /**
   * Get <ArticleImg>.
   */
  getArticleImage() {
    const {
      context,
      thumbnail,
      lead_media: leadMedia,
      media_type_icon: mediaTypeIcon,
      default_ap_image, // eslint-disable-line camelcase
    } = this.props;
    const { imageContext } = this.state;
    const {
      0: proportions,
      1: sizes,
    } = imageContext;
    const hasThumb = !! (thumbnail && thumbnail.src);
    const img = hasThumb
      ? withProprtionalResponsiveAttributes(thumbnail, proportions, sizes)
      : null;

    return (
      <>
        {(img) ? (
          <ArticleImg
            src={img.src}
            alt={img.alt_text}
            srcset={img.srcset}
            sizes={img.sizes}
            orientation={img.orientation}
            proportions={proportions}
            context={context}
            leadMedia={leadMedia}
            mediaTypeIcon={mediaTypeIcon}
          />
        ) : (
          (default_ap_image && ( // eslint-disable-line camelcase
            <ArticleImg
              src={default_ap_image.ap_default_src}
              alt={default_ap_image.ap_default_alt_text}
              orientation={default_ap_image.ap_default_orientation}
              proportions={proportions}
              context={context}
              leadMedia={leadMedia}
              mediaTypeIcon={mediaTypeIcon}
            />
          ))
        )}
      </>
    );
  }

  render() {
    const {
      context,
      media_type_icon: mediaTypeIcon,
      h,
      link,
      title,
      excerpt_field, // eslint-disable-line camelcase
      thumbnail,
      category,
      date,
      site_model, // eslint-disable-line camelcase
      showThumb,
      default_ap_image, // eslint-disable-line camelcase
    } = this.props;

    const {
      articleTextClasses,
      articleThumbClasses,
    } = this.state;

    const Heading = `h${h}`;
    const {
      template_variables, // eslint-disable-line camelcase
    } = site_model; // eslint-disable-line camelcase

    const hasCategory = !! (category && category.title);
    const hasDate = !! (date && date.time); // eslint-disable-line react/prop-types
    const hasThumb = !! (thumbnail && thumbnail.src);
    const noImage = 'headline_list_text_only--is_infinite' === context
    || 'headline_list_two_col--is_infinite' === context
    || 'headline_list_three_col--is_infinite' === context;
    const showImage = showThumb && ! noImage;
    const defaultIcon = { name: 'default', label: 'default' };
    const icon = ('object' === typeof mediaTypeIcon && '' !== mediaTypeIcon.name) ? mediaTypeIcon : defaultIcon;
    const showExcerpt = 'featured--is_infinite' === context || 'headline_list_one_col_image_branded--is_infinite' === context;

    return (
      <>
        <div className={articleTextClasses}>
          {title && (
            <Heading className="article-list__article-title">
              {link
                ? (
                  <a
                    href={link}
                    className="article-list__article-link"
                  >
                    {parse(DOMPurify.sanitize(title))}
                  </a>
                ) : title}
            </Heading>
          )}

          {
            (hasCategory && hasDate && site_model && ( // eslint-disable-line camelcase
              <footer className="article-list__article-meta">
                {parse(DOMPurify.sanitize(
                  template_variables
                    .l10n
                    .article_list_category
                    .replace(
                      '%1$s',
                      `<a
                          href="${category.link}"
                          class="article-list__article-category-link">
                          ${parse(DOMPurify.sanitize(category.title))}
                        </a>`,
                    )
                    .replace(
                      '%2$s',
                      `<time datetime="${date.datetime}">${date.time}</time>`, // eslint-disable-line react/prop-types
                    ),
                ))}
              </footer>
            ))
            || (hasCategory && (
              <footer className="article-list__article-meta">
                <a
                  href={category.link}
                  className="article-list__article-category-link"
                >
                  {parse(DOMPurify.sanitize(category.title))}
                </a>
              </footer>
            ))
            || (hasDate && (
              <footer className="article-list__article-meta">
                {/* eslint-disable-next-line react/prop-types */}
                <time dateTime={date.datetime}>{date.time}</time>
              </footer>
            ))
          }
          {excerpt_field && showExcerpt && ( // eslint-disable-line camelcase
            <div className="article-list__article-excerpt">
              <a
                href={link}
                className="article-list__article-link"
              >
                <p>
                  {parse(DOMPurify.sanitize(excerpt_field.replace(/(<([^>]+)>)/gi, '')))}
                </p>
              </a>
            </div>
          )}
          {noImage && (
            <ArticleMediaIcon mediaTypeIcon={icon} />
          )}
        </div>

        {(showImage || default_ap_image) ? ( // eslint-disable-line camelcase
          <figure className={articleThumbClasses}>
            {link ? (
              <a href={link} className="article-list__article-link">
                {hasThumb ? (
                  this.getArticleImage()
                ) : (
                  <div className="article-list__article-image-wrapper">
                    <div className="image-wrapper no-image" />
                    {'default' !== icon.name ? (
                      <ArticleMediaIcon mediaTypeIcon={icon} />
                    ) : null}
                  </div>
                )}
              </a>
            ) : (
              <div>
                {hasThumb ? (
                  this.getArticleImage()
                ) : (
                  <div className="article-list__article-image-wrapper">
                    <div className="image-wrapper no-image" />
                    {'default' !== icon.name ? (
                      <ArticleMediaIcon mediaTypeIcon={icon} />
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </figure>
        ) : (
          <div />
        )}
      </>
    );
  }
}
